import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState, useMemo } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { Map, Iterable } from 'immutable';

import { checkLocalStorage, getItem, setItem } from 'helpers/local_storage';
import { secondsToString } from 'helpers/time';

function CoundownTimer({ id, time, onFinish, paused }) {
  if (!time) return null;

  const theme = useTheme();

  const [state, setState] = useState(new Map());
  const remaining = state.get('remaining', null);
  const stopsAt = state.get('stopsAt', null);
  const finished = state.get('finished', false); // Ensure onFinish is called just once

  useEffect(() => {
    if (!checkLocalStorage()) return;

    let stopsAt = getItem(id);
    if (!stopsAt) {
      const date = new Date();
      date.setSeconds(date.getSeconds() + time);

      stopsAt = date.getTime() / 1000;
      setItem(id, stopsAt);
    }

    if (stopsAt < (new Date()).getTime() / 1000) {
      setTimeout(onFinish, 100);
      return;
    }

    setState(state.set('stopsAt', stopsAt));
  }, []);

  useEffect(() => {
    if (!stopsAt) return;

    const interval = setInterval(() => {
      const currentTime = (new Date()).getTime() / 1000;
      setState(prev => prev.set('remaining', Math.round(stopsAt - currentTime)));
    }, 1000);

    return () => {
      if (interval) clearInterval(interval);
    }
  }, [stopsAt]);

  useEffect(() => {
    if (remaining === null || finished) return;

    // Case when the blobs are uploading we will pause the timer
    if (paused) {
      setState(state.set('stopsAt', stopsAt + 1));
      return;
    }

    if (remaining <= 0) {
      setState(state.set('finished', true));
      onFinish();
    }
  }, [remaining, finished]);

  const label = useMemo(() => {
    if (remaining <= 0) return;

    return secondsToString(remaining);
  }, [remaining, finished]);

  if (!label) return null;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ px: 4, borderRadius: '20px', borderTop: 0, borderBottom: 0, borderLeft: '1px solid transparent', borderRight: '1px solid transparent', py: 1, backgroundColor: theme.palette.background.dark }}>
        <Typography sx={{ m: 0, px: 5 }} data-testid="countdown-timer-label">{label}</Typography>
      </Box>
    </Box>
  );
}

CoundownTimer.propTypes = {
  id: PropTypes.string,
  time: PropTypes.number,
  onFinish: PropTypes.func,
  paused: PropTypes.bool,
};

export default CoundownTimer;
